import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface SlideCarouselProps {
  urls: string[];
  className?: string;
}

export function SlideCarousel({ urls, className }: SlideCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setActive(Math.max(0, Math.min(urls.length - 1, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [urls.length]);

  // New render (style switch) → jump back to the first slide.
  useEffect(() => {
    scrollerRef.current?.scrollTo({ left: 0 });
    setActive(0);
  }, [urls]);

  function scrollTo(idx: number) {
    const el = scrollerRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(urls.length - 1, idx));
    el.scrollTo({ left: target * el.clientWidth, behavior: 'smooth' });
  }

  if (urls.length === 0) {
    return (
      <div
        className={cn(
          'aspect-[4/5] w-full rounded-2xl bg-tg-section-bg flex items-center justify-center text-sm text-tg-hint',
          className,
        )}
      >
        Слайды ещё не собраны
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="relative">
        <div
          ref={scrollerRef}
          className="flex overflow-x-auto snap-x snap-mandatory -mx-4 px-4 scrollbar-none"
        >
          {urls.map((url, idx) => (
            <div key={`${url}-${idx}`} className="snap-center shrink-0 w-full pr-2 last:pr-0">
              <img
                src={url}
                alt={`Слайд ${idx + 1}`}
                className="w-full rounded-2xl aspect-[4/5] object-cover bg-tg-section-bg"
                loading={idx < 2 ? 'eager' : 'lazy'}
              />
            </div>
          ))}
        </div>
        {urls.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => scrollTo(active - 1)}
              disabled={active === 0}
              aria-label="Предыдущий слайд"
              className="absolute left-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-30"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollTo(active + 1)}
              disabled={active === urls.length - 1}
              aria-label="Следующий слайд"
              className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>
      <p className="text-xs text-tg-hint text-center mt-2 tabular-nums">
        {active + 1} / {urls.length}
      </p>
    </div>
  );
}
